import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { BaseFormComponent } from '@core/base/base-form.component';
import { AuthService } from '@core/services/auth.service';
import { AuthStore } from '@core/stores/auth.store';
import { LucideSave } from '@lucide/angular';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { PasswordFieldComponent } from '@shared/components/password-field/password-field.component';
import {
  ChangePasswordModel,
  changePasswordSchema,
} from './change-password-schema';

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [TranslateModule, PasswordFieldComponent, LucideSave],
  templateUrl: './change-password-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordModalComponent extends BaseFormComponent {
  readonly activeModal = inject(NgbActiveModal);
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);

  readonly userName = computed(() => this.authStore.user()?.user_name || '');

  readonly model = signal<ChangePasswordModel>({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  readonly changePasswordForm = this.formWithServerErrors(
    this.model,
    changePasswordSchema,
  );

  async onSubmit(event: Event) {
    event.preventDefault();

    if (this.changePasswordForm().invalid()) return;

    const data = this.model();

    await this.handleSubmit(
      async () => {
        await this.authService.changePassword({
          old_password: data.old_password,
          new_password: data.new_password,
          confirm_password: data.confirm_password,
        });

        this.toast.showSuccess(
          this.translate.instant('COMMON.SUCCESS'),
          this.translate.instant('CHANGE_PASSWORD.SUCCESS'),
        );
        this.activeModal.close(true);
        this.authService.logout();
      },
      { showError: true },
    );
  }
}
